import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2, Plus, Calendar as CalendarIcon, X, Clock, Repeat } from 'lucide-react';
import { format, addHours, addMinutes } from 'date-fns';
import api, { getErrorMessage } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from './ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Calendar } from './ui/calendar';
import { Switch } from './ui/switch';

const taskSchema = z.object({
  title: z.string().min(1, 'Task title is required'),
  description: z.string().optional(),
  category: z.string().min(1, 'Category is required'),
  priority: z.string().min(1, 'Priority is required'),
});

const PRIORITY_OPTIONS = [
  { value: 'HIGH', label: 'High' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'LOW', label: 'Low' },
];
const TIME_UNIT_OPTIONS = [
  { value: 'MINUTES', label: 'Minutes' },
  { value: 'HOURS', label: 'Hours' },
];
const TASK_TYPE_OPTIONS = [
  { value: 'INSTANT', label: 'Instant Task' },
  { value: 'RECURRING', label: 'Recurring Task' },
];

export default function CreateTaskModal({ open, onClose, onSuccess }) {
  const modalRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [selectedTime, setSelectedTime] = useState(() => {
    try {
      return format(new Date(), 'HH:mm');
    } catch {
      return '09:00';
    }
  });
  const [selectedStaff, setSelectedStaff] = useState('');
  const [showTemplateList, setShowTemplateList] = useState(false);
  const [category, setCategory] = useState('Other');
  const [priority, setPriority] = useState('MEDIUM');
  const [taskType, setTaskType] = useState('INSTANT');
  const [timeInterval, setTimeInterval] = useState(30);
  const [timeUnit, setTimeUnit] = useState('MINUTES');
  
  // Recurring task state
  const [recurrenceStartDate, setRecurrenceStartDate] = useState(null);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  const [frequencyDays, setFrequencyDays] = useState(1);
  const [isInfinite, setIsInfinite] = useState(true);
  const [weekdays, setWeekdays] = useState([]);
  const [scheduleMode, setScheduleMode] = useState('frequency'); // 'frequency' | 'weekday_groups'
  const [weekdayTimes, setWeekdayTimes] = useState([{ weekdays: [], time: '09:00' }]);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: zodResolver(taskSchema),
    defaultValues: {
      title: '',
      description: '',
      category: 'Other',
      priority: 'MEDIUM',
    },
  });

  const titleValue = watch('title');

  useEffect(() => {
    if (open) {
      fetchTemplates();
      fetchStaff();
      fetchCategories();
      document.body.style.overflow = 'hidden';
      // Set default allocated time to now
      setSelectedDate(new Date());
      try {
        setSelectedTime(format(new Date(), 'HH:mm'));
      } catch {
        setSelectedTime('09:00');
      }
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const fetchTemplates = async (search = '') => {
    try {
      const response = await api.get(`/task-templates?search=${search}`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await api.get('/users/staff');
      setStaffList(response.data);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleTemplateSelect = (template) => {
    setValue('title', template.title);
    if (template.category) {
      setValue('category', template.category);
      setCategory(template.category);
    }
    if (template.priority) {
      setValue('priority', template.priority);
      setPriority(template.priority);
    }
    setShowTemplateList(false);
  };

  const handleAddNewTemplate = async () => {
    if (!titleValue?.trim()) return;
    
    try {
      await api.post('/task-templates', { title: titleValue });
      toast.success('Task added to library');
      fetchTemplates();
    } catch (error) {
      // If already exists, that's fine
    }
    setShowTemplateList(false);
  };


  const onSubmit = async (data) => {
    if (taskType === 'RECURRING') {
      if (!recurrenceStartDate) {
        toast.error('Start date is required for recurring tasks');
        return;
      }
      if (scheduleMode === 'frequency' && (!frequencyDays || frequencyDays < 1)) {
        toast.error('Frequency must be at least 1 day');
        return;
      }
      if (scheduleMode === 'weekday_groups' && !weekdayTimes.some(g => g.weekdays.length > 0)) {
        toast.error('Select at least one day in a time group');
        return;
      }
    }

    setLoading(true);
    try {
      // Combine date and time for allocated_datetime
      const [hours, minutes] = selectedTime.split(':');
      const allocatedDateTime = new Date(selectedDate);
      allocatedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
      
      const taskData = {
        title: data.title,
        description: data.description || '',
        category: category,
        priority: priority,
        task_type: taskType,
        time_interval: parseInt(timeInterval),
        time_unit: timeUnit,
        allocated_datetime: allocatedDateTime.toISOString(),
        assigned_to: selectedStaff || null,
      };
      
      if (taskType === 'RECURRING') {
        taskData.recurrence_type = 'DATE_RANGE';
        taskData.recurrence_start_date = recurrenceStartDate ? format(recurrenceStartDate, 'yyyy-MM-dd') : null;
        taskData.recurrence_end_date = (!isInfinite && recurrenceEndDate) ? format(recurrenceEndDate, 'yyyy-MM-dd') : null;
        if (scheduleMode === 'weekday_groups') {
          taskData.weekday_times = weekdayTimes.filter(g => g.weekdays.length > 0);
          taskData.weekdays = null;
          taskData.frequency_days = null;
        } else {
          taskData.weekday_times = null;
          taskData.weekdays = weekdays.length > 0 ? weekdays : null;
          taskData.frequency_days = parseInt(frequencyDays) || 1;
        }
        taskData.recurrence_intervals = [];
      }
      
      await api.post('/tasks', taskData);
      toast.success('Task created successfully');
      handleClose();
      onSuccess();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create task'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setSelectedDate(new Date());
    try {
      setSelectedTime(format(new Date(), 'HH:mm'));
    } catch {
      setSelectedTime('09:00');
    }
    setSelectedStaff('');
    setCategory('Other');
    setPriority('MEDIUM');
    setTaskType('INSTANT');
    setTimeInterval(30);
    setTimeUnit('MINUTES');
    setRecurrenceStartDate(null);
    setRecurrenceEndDate(null);
    setFrequencyDays(1);
    setIsInfinite(true);
    setWeekdays([]);
    setScheduleMode('frequency');
    setWeekdayTimes([{ weekdays: [], time: '09:00' }]);
    setShowTemplateList(false);
    onClose();
  };

  // Calculate deadline preview
  const getDeadlinePreview = () => {
    try {
      if (!selectedTime || !selectedDate) return new Date();
      const timeParts = selectedTime.split(':');
      if (timeParts.length !== 2) return new Date();
      
      const hours = parseInt(timeParts[0]) || 0;
      const minutes = parseInt(timeParts[1]) || 0;
      const allocatedDateTime = new Date(selectedDate);
      
      if (isNaN(allocatedDateTime.getTime())) return new Date();
      
      allocatedDateTime.setHours(hours, minutes, 0, 0);
      
      const interval = parseInt(timeInterval) || 30;
      if (timeUnit === 'HOURS') {
        return addHours(allocatedDateTime, interval);
      }
      return addMinutes(allocatedDateTime, interval);
    } catch (e) {
      return new Date();
    }
  };

  const filteredTemplates = templates.filter(t => 
    (t.title || '').toLowerCase().includes(titleValue?.toLowerCase() || '')
  );

  const showAddToLibrary = titleValue && 
    !templates.some(t => (t.title || '').toLowerCase() === titleValue.toLowerCase());

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-task-modal">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
        data-testid="modal-backdrop"
      />
      
      {/* Modal Content */}
      <div 
        ref={modalRef}
        className="relative z-50 w-full max-w-md mx-4 bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-zinc-900">Create New Task</h2>
          <button 
            onClick={handleClose}
            className="p-2 hover:bg-zinc-100 rounded-full transition-colors"
            data-testid="close-modal-btn"
          >
            <X className="h-5 w-5 text-zinc-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">

          {/* Recurring Task Schedule */}
          {taskType === 'RECURRING' && (
            <div className="space-y-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <Label className="text-blue-800 flex items-center gap-2">
                <Repeat className="h-4 w-4" />
                Recurring Schedule
              </Label>

              {/* Start Date */}
              <div className="space-y-1">
                <Label className="text-sm">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="w-full justify-start rounded-xl bg-white" data-testid="recurrence-start-date-btn">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {recurrenceStartDate ? format(recurrenceStartDate, 'MMM d, yyyy') : 'Pick a start date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={recurrenceStartDate}
                      onSelect={(d) => setRecurrenceStartDate(d || null)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* End Date + Infinite toggle */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">End Date</Label>
                  <div className="flex items-center gap-2 text-sm text-blue-700">
                    <span>Runs forever</span>
                    <Switch
                      checked={isInfinite}
                      onCheckedChange={(v) => { setIsInfinite(v); if (v) setRecurrenceEndDate(null); }}
                      data-testid="infinite-toggle"
                    />
                  </div>
                </div>
                {!isInfinite && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-start rounded-xl bg-white" data-testid="recurrence-end-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {recurrenceEndDate ? format(recurrenceEndDate, 'MMM d, yyyy') : 'Pick an end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={recurrenceEndDate}
                        onSelect={(d) => setRecurrenceEndDate(d || null)}
                        disabled={(d) => recurrenceStartDate && d < recurrenceStartDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>

              {/* Schedule Mode Toggle */}
              <div className="flex gap-2">
                <button type="button"
                  onClick={() => setScheduleMode('frequency')}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium border transition-colors ${
                    scheduleMode === 'frequency'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-zinc-600 border-zinc-200'
                  }`}
                  data-testid="mode-frequency-btn"
                >Every N days</button>
                <button type="button"
                  onClick={() => setScheduleMode('weekday_groups')}
                  className={`flex-1 py-2 rounded-xl text-xs font-medium border transition-colors ${
                    scheduleMode === 'weekday_groups'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-zinc-600 border-zinc-200'
                  }`}
                  data-testid="mode-weekday-btn"
                >By weekday</button>
              </div>

              {/* Frequency mode */}
              {scheduleMode === 'frequency' && (
                <>
                  {/* Frequency */}
                  <div className="space-y-1">
                    <Label className="text-sm">Repeat every</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        value={frequencyDays}
                        onChange={(e) => setFrequencyDays(parseInt(e.target.value) || 1)}
                        className="w-24 rounded-xl bg-white"
                        data-testid="frequency-days-input"
                      />
                      <span className="text-sm text-blue-700">
                        {frequencyDays === 1 ? 'day (daily)' : frequencyDays === 7 ? 'days (weekly)' : 'days'}
                      </span>
                    </div>
                  </div>

                  {/* Weekday Selector */}
                  <div className="space-y-1">
                    <Label className="text-sm">Repeat on (optional)</Label>
                    <div className="flex gap-1 flex-wrap">
                      {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, i) => (
                        <button key={i} type="button"
                          onClick={() => {
                            const days = weekdays.includes(i)
                              ? weekdays.filter(d => d !== i)
                              : [...weekdays, i];
                            setWeekdays(days);
                          }}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                            weekdays.includes(i)
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-zinc-600 border border-zinc-200 hover:border-blue-400'
                          }`}
                          data-testid={`weekday-btn-${i}`}
                        >{day}</button>
                      ))}
                    </div>
                    <p className="text-xs text-zinc-400">
                      {weekdays.length === 0 ? 'No filter — uses frequency above' : `${weekdays.length} day(s) selected — frequency ignored`}
                    </p>
                  </div>

                  {/* Preview */}
                  {recurrenceStartDate && (weekdays.length > 0 || frequencyDays) && (
                    <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded-lg">
                      {weekdays.length > 0
                        ? `Every ${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].filter((_,i) => weekdays.includes(i)).join(', ')}`
                        : `Starting ${format(recurrenceStartDate, 'MMM d, yyyy')}, runs every ${frequencyDays} day${frequencyDays !== 1 ? 's' : ''}`}
                      {isInfinite ? ', forever' : recurrenceEndDate ? ` until ${format(recurrenceEndDate, 'MMM d, yyyy')}` : ''}
                    </div>
                  )}
                </>
              )}

              {/* Weekday groups mode */}
              {scheduleMode === 'weekday_groups' && (
                <div className="space-y-2">
                  {weekdayTimes.map((group, gi) => (
                    <div key={gi} className="p-2 bg-white rounded-xl border border-zinc-200 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-zinc-500 font-medium">Group {gi + 1}</span>
                        {weekdayTimes.length > 1 && (
                          <button type="button"
                            onClick={() => setWeekdayTimes(weekdayTimes.filter((_,i) => i !== gi))}
                            className="text-xs text-red-400 hover:text-red-600"
                            data-testid={`remove-group-${gi}`}
                          >Remove</button>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map((day, di) => (
                          <button key={di} type="button"
                            onClick={() => {
                              const newGroups = [...weekdayTimes];
                              const days = newGroups[gi].weekdays.includes(di)
                                ? newGroups[gi].weekdays.filter(d => d !== di)
                                : [...newGroups[gi].weekdays, di];
                              newGroups[gi] = { ...newGroups[gi], weekdays: days };
                              setWeekdayTimes(newGroups);
                            }}
                            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                              group.weekdays.includes(di)
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-zinc-600 border border-zinc-200 hover:border-blue-400'
                            }`}
                            data-testid={`group-${gi}-day-${di}`}
                          >{day}</button>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-zinc-400" />
                        <Input type="time" value={group.time}
                          onChange={(e) => {
                            const newGroups = [...weekdayTimes];
                            newGroups[gi] = { ...newGroups[gi], time: e.target.value };
                            setWeekdayTimes(newGroups);
                          }}
                          className="rounded-lg bg-white h-9 w-32"
                          data-testid={`group-${gi}-time`}
                        />
                        <span className="text-xs text-zinc-400">
                          {group.weekdays.length > 0
                            ? ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].filter((_,i) => group.weekdays.includes(i)).join(', ')
                            : 'No days selected'}
                        </span>
                      </div>
                    </div>
                  ))}
                  <button type="button"
                    onClick={() => setWeekdayTimes([...weekdayTimes, { weekdays: [], time: '09:00' }])}
                    className="w-full py-2 rounded-xl text-xs text-blue-600 border border-dashed border-blue-300 hover:bg-blue-50"
                    data-testid="add-group-btn"
                  >+ Add another time group</button>
                  {recurrenceStartDate && weekdayTimes.some(g => g.weekdays.length > 0) && (
                    <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded-lg">
                      {weekdayTimes.filter(g => g.weekdays.length > 0).map((g) =>
                        `${['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].filter((_,i) => g.weekdays.includes(i)).join('/')} at ${g.time}`
                      ).join(' · ')}
                      {isInfinite ? ', forever' : recurrenceEndDate ? ` until ${format(recurrenceEndDate, 'MMM d, yyyy')}` : ''}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Task Title with Autocomplete */}
          <div className="space-y-2">
            <Label htmlFor="title">Task Name</Label>
            <div className="relative">
              <Input
                id="title"
                placeholder="Start typing or select from library..."
                className="rounded-xl"
                {...register('title')}
                onFocus={() => setShowTemplateList(true)}
                onBlur={() => setTimeout(() => setShowTemplateList(false), 200)}
                data-testid="task-title-input"
              />
              
              {showTemplateList && titleValue && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-xl shadow-lg z-[60] max-h-48 overflow-y-auto">
                  {filteredTemplates.map(template => (
                    <div
                      key={template.id}
                      className="px-4 py-2 hover:bg-zinc-50 cursor-pointer text-sm"
                      onClick={() => handleTemplateSelect(template)}
                      data-testid={`template-option-${template.id}`}
                    >
                      {template.title}
                    </div>
                  ))}
                  {showAddToLibrary && (
                    <div
                      className="px-4 py-2 hover:bg-zinc-50 cursor-pointer text-sm text-[#E23744] border-t border-zinc-100 flex items-center gap-2"
                      onClick={handleAddNewTemplate}
                      data-testid="add-to-library-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Add "{titleValue}" to Task Library
                    </div>
                  )}
                  {filteredTemplates.length === 0 && !showAddToLibrary && (
                    <div className="px-4 py-2 text-sm text-zinc-400">
                      No templates found
                    </div>
                  )}
                </div>
              )}
            </div>
            {errors.title && (
              <p className="text-sm text-red-500">{errors.title.message}</p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description (Optional)</Label>
            <Textarea
              id="description"
              placeholder="Add task details..."
              className="rounded-xl resize-none"
              rows={2}
              {...register('description')}
              data-testid="task-description-input"
            />
          </div>

          {/* Category & Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={category}
                onValueChange={(v) => {
                  setCategory(v);
                  setValue('category', v);
                }}
              >
                <SelectTrigger className="rounded-xl" data-testid="task-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.length > 0 ? categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  )) : (
                    <>
                      <SelectItem value="Kitchen">Kitchen</SelectItem>
                      <SelectItem value="Cleaning">Cleaning</SelectItem>
                      <SelectItem value="Maintenance">Maintenance</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select 
                value={priority}
                onValueChange={(v) => {
                  setPriority(v);
                  setValue('priority', v);
                }}
              >
                <SelectTrigger className="rounded-xl" data-testid="task-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Time Interval */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Time Allowed (Required)
            </Label>
            <div className="grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="1"
                max="1440"
                value={timeInterval}
                onChange={(e) => setTimeInterval(e.target.value)}
                className="rounded-xl"
                data-testid="time-interval-input"
              />
              <Select value={timeUnit} onValueChange={setTimeUnit}>
                <SelectTrigger className="rounded-xl" data-testid="time-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_UNIT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Allocated Date & Time - Only for Instant tasks */}
          {taskType === 'INSTANT' && (
            <>
              <div className="space-y-2">
                <Label>Start Date & Time</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start text-left font-normal rounded-xl h-12"
                        data-testid="task-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(selectedDate, 'MMM d, yyyy')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={(date) => date && setSelectedDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <Input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    className="rounded-xl h-12"
                    data-testid="task-time-input"
                  />
                </div>
              </div>

              {/* Deadline Preview */}
              <div className="bg-zinc-50 rounded-xl p-3 text-sm">
                <div className="flex items-center gap-2 text-zinc-600">
                  <Clock className="h-4 w-4" />
                  <span>Deadline will be:</span>
                  <span className="font-medium text-zinc-900">
                    {format(getDeadlinePreview(), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              </div>
            </>
          )}

          {/* For recurring tasks, show daily time */}
          {taskType === 'RECURRING' && (
            <div className="space-y-2">
              <Label>Daily Start Time</Label>
              <Input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="rounded-xl h-12"
                data-testid="recurring-time-input"
              />
              <p className="text-xs text-zinc-500">
                Task will appear at this time each scheduled day with {timeInterval} {timeUnit.toLowerCase()} to complete
              </p>
            </div>
          )}

          {/* Assign To */}
          <div className="space-y-2">
            <Label>Assign To (Optional)</Label>
            <Select value={selectedStaff} onValueChange={setSelectedStaff}>
              <SelectTrigger className="rounded-xl" data-testid="task-assign-select">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {staffList.map(staff => (
                  <SelectItem key={staff.id} value={staff.id}>
                    {staff.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#E23744] hover:bg-[#C42B37] text-white rounded-full"
            data-testid="submit-task-btn"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              'Create Task'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
